import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"

import {
  AlertContentItem,
  AlertType,
  BacklogSetup,
  GetItemCurrentPosition,
  HttpError,
  InsertItemInBacklog,
  Item,
  ItemCurrentPositionResponse,
  ItemMoveTo,
  ItemQueryPriority,
  ItemStatus,
  ItemType,
  RemoveItemFromBacklog,
  ServerSendEventNotification,
  ShowAlertConfig,
  Sprint,
  SprintInfo,
  Status,
  Task,
  UpdateObjectValue,
  UpdateStatusParams
} from "@/models"
import { $t } from "@/plugins/i18n"
import { Actions, Getters, store } from "@/store"
import { AlertActions, alertModule } from "@/store/modules/alert.module"
import { TeamHasActiveSprint } from "@/store/modules/team.module"
import { excludedStatusFromBacklog } from "@/utils"

@Options({
  name: "moveTo"
})
export class moveTo extends Vue {
  @Prop({ required: true }) readonly backlogSetup: BacklogSetup
  @Getter(Getters.GET_CURRENT_PROJECT_ID) readonly sessionProjectId: string

  @Getter(Getters.GET_ITEM) item: Item
  @Getter(Getters.GET_TASKS) tasks: Task[]
  @Getter(Getters.MOVE_ITEM_FROM_ACTIVE_SPRINT) readonly moveItemFromActiveSprint: ItemMoveTo | null
  @Getter(Getters.TEAM_HAS_ACTIVE_SPRINT) readonly teamHasActiveSprint: TeamHasActiveSprint
  @Getter(Getters.GET_UNPRIORITIZED_FILTER) readonly unprioritizedFilter: string
  @Getter(Getters.GET_MAIN_BACKLOG_SETUP) readonly mainBacklogSetup: BacklogSetup

  @Action(Actions.MOVE_ITEM_FROM_ACTIVE_SPRINT) setMoveItemFromActiveSprint: (
    value: ItemMoveTo | null
  ) => void
  @Action(Actions.SHOW_ALERT) showToast: (params: ShowAlertConfig) => void
  @Action(Actions.CHECK_WIP_LIMIT) checkWipLimit: ({ itemId, sprintId }) => Promise<boolean>
  @Action(Actions.UPDATE_ITEM_VALUES) updateOpenedItemValues: ({
    payload,
    updateClone
  }: {
    payload: UpdateObjectValue<Item>[]
    updateClone?: boolean
  }) => void
  @Action(Actions.GET_BACKLOG_CREATED_ITEM) setCreatedBacklogItem: (item: Item | null) => void
  @Action(Actions.INSERT_BACKLOG_ITEM) insertItem: (payload: InsertItemInBacklog) => void
  @Action(Actions.REMOVE_ITEM_FROM_BACKLOG) removeItemFromBacklog: (
    params: RemoveItemFromBacklog
  ) => void
  @Action(Actions.UPDATE_ITEM_VALUE) updateItemValue: (params: UpdateObjectValue<Item>) => void
  @Action(Actions.UPDATE_ITEM_CLONE_VALUE) updateItemCloneValue: (
    params: UpdateObjectValue<Item>
  ) => void
  @Action(Actions.SET_ITEM) setItem: (item: Item) => void
  @Action(Actions.UPDATE_ITEM_DETAILS) updateItem: ({
    item,
    notSetItem
  }: {
    item: Item
    notSetItem?: boolean
  }) => Promise<Item | false>
  @Action(Actions.GET_ITEM) getItem: ({
    itemId,
    onlyGet,
    projectId
  }: {
    itemId: string
    onlyGet?: boolean
    projectId?: string
  }) => Promise<Item | HttpError>
  @Action(Actions.GET_SPRINT) getSprint: ({
    sprintId,
    onlyGet
  }: {
    sprintId: string
    onlyGet
  }) => Promise<Sprint>
  @Action(Actions.TEAM_HAS_ACTIVE_SPRINT) hasActiveSprintCheck: ({
    teamId,
    projectId
  }: {
    teamId: string
    projectId: string
  }) => Promise<boolean>
  @Action(Actions.SET_MODAL_TYPE_FOR_CREATING_NEW_TASK) onShowModalCreateTask: (
    statusType: string
  ) => void
  @Action(Actions.GET_ITEM_CURRENT_POSITION) getItemCurrentPosition: (
    payload: GetItemCurrentPosition
  ) => Promise<ItemCurrentPositionResponse>

  canMoveToActiveSprint = true
  itemMoveToParams: null | InsertItemInBacklog = null
  sprintWipLimitExceeded: SprintInfo | null = null
  removeItemFromSprintModalId = "remove-item-from-active-sprint"
  moveToActiveSprintModalId = "move-item-to-active-sprint"
  pendingMoveToActiveSprintStatus: Status | null = null
  currentItemIndex: number | null = null
  currentItemIndexRequestId = 0
  readonly currentItemIndexWaitTitle = "loadingCurrentItemIndex"

  get estimatedTasks() {
    return Task.getEstimatedTasksCount({ tasks: this.tasks })
  }

  get priorityTooltipText() {
    if (this.item.sprint) {
      return this.item.sprint?.status === "STARTED"
        ? "Active Sprint Backlog"
        : `Sprint #${this.item.sprint?.sequenceNumber} Backlog`
    }
    return `${$t(`backlog.titles['${this.backlogTypeByItem}']`).replaceAll("Backlog", "")} Backlog`
  }

  get backlogType() {
    return this.backlogSetup?.backlogType ?? this.backlogTypeByItem
  }

  get backlogTypeByItem() {
    return Item.getBacklogTypeByItem(this.item)
  }

  get projectId() {
    return this.item.projectId ? this.item.projectId : this.sessionProjectId
  }

  get moveToActiveSprintActionType() {
    return this.pendingMoveToActiveSprintStatus?.id === "RESUME" ? "resume" : "start"
  }

  getCurrentItemPositionFilter() {
    if (!this.item.id) return null

    const backlogSetupFactory = {
      backlogProduct: () => new BacklogSetup("backlogProduct"),
      backlogBug: () => new BacklogSetup("backlogBug"),
      backlogSprint: () => new BacklogSetup("backlogSprint"),
      backlogSupportCases: () => new BacklogSetup("backlogSupportCases"),
      backlogTechDebt: () => new BacklogSetup("backlogTechDebt"),
      backlogDesignDebt: () => new BacklogSetup("backlogDesignDebt"),
      backlogDev: () => new BacklogSetup("backlogDev"),
      backlogDevOps: () => new BacklogSetup("backlogDevOps"),
      backlogOps: () => new BacklogSetup("backlogOps")
    }[this.backlogTypeByItem]

    if (!backlogSetupFactory) return null

    const backlogSetup = backlogSetupFactory()

    if (this.item.team?.id) {
      backlogSetup.filter.teamIds = [this.item.team.id]
    }

    if (this.projectId) {
      backlogSetup.filter.currentProjectId = this.projectId
    }

    if (this.item.sprint?.id) {
      backlogSetup.filter.sprintIds = [this.item.sprint.id]
    }

    backlogSetup.filter.withoutPagination = true

    return backlogSetup.filter.createCurrentPositionFilter()
  }

  async loadCurrentItemIndex() {
    if (this.item.backlogPriority === null) {
      this.currentItemIndex = null
      return
    }

    const filter = this.getCurrentItemPositionFilter()

    if (!this.item.id || !this.projectId || !filter) {
      this.currentItemIndex = null
      return
    }

    const requestId = ++this.currentItemIndexRequestId
    this.$wait.start(this.currentItemIndexWaitTitle)

    try {
      const { position, offsetPosition } = await this.getItemCurrentPosition({
        projectId: this.projectId,
        itemId: this.item.id,
        filter
      })

      if (requestId !== this.currentItemIndexRequestId) {
        return
      }

      this.currentItemIndex = offsetPosition === -1 ? null : position + 1
    } finally {
      this.$wait.end(this.currentItemIndexWaitTitle)
    }
  }

  async loadCurrentItemIndexBySSE(event: ServerSendEventNotification) {
    if (!event?.item?.id || event.item.id !== this.item.id) return

    const isPriorityChanged =
      event.type === "ITEM_PRIORITY_CHANGED" || Boolean(event.changedPriorities?.length)

    if (!isPriorityChanged) return

    await this.loadCurrentItemIndex()
  }

  private getTargetBacklogFilter(backlogName: string, sprintId?: string) {
    const isMainMatched =
      this.mainBacklogSetup?.backlogType === backlogName &&
      (backlogName !== "backlogSprint" || this.mainBacklogSetup?.sprintInfo?.id === sprintId)

    if (isMainMatched) {
      return this.mainBacklogSetup.filter
    }

    const isCurrentMatched =
      this.backlogSetup?.backlogType === backlogName &&
      (backlogName !== "backlogSprint" || this.backlogSetup?.sprintInfo?.id === sprintId)

    if (isCurrentMatched) {
      return this.backlogSetup.filter
    }

    // fallback only when target setup is not available in current context
    const fallback = new BacklogSetup(backlogName as any)
    if (backlogName === "backlogSprint" && sprintId) {
      fallback.filter.sprintIds = [sprintId]
      fallback.filter.inSprint = true
    }
    return fallback.filter
  }

  async get({
    itemId,
    onlyGet,
    projectId
  }: {
    itemId: string
    onlyGet?: boolean
    projectId?: string
  }) {
    const isItem = await this.getItem({ itemId, onlyGet, projectId })
    if (isItem instanceof HttpError && isItem.response?.status === 404) {
      this.$router.replace({
        name: "Error",
        query: {
          code: "404I",
          t: this.$route.params.itemId as string
        }
      })
    }
  }

  async onMoveToAndValid(payload: ItemMoveTo) {
    const { toSprintStatus, item, toSprintInfo } = payload
    if (!item.id) return
    this.updateItemValue({
      id: "sprintIsContinuous",
      value: !!toSprintInfo?.continuousSprint
    } as any)

    const sprintValidationCallback = () => {
      if (toSprintStatus === "STARTED" && toSprintInfo) {
        this.setCreatedBacklogItem(Object.assign(item, { moveFrom: "moveTo" }))
        return this.canAddItemIntoActiveSprint()
      }
    }

    await this.sprintValidationByMethodAndAlert(payload, sprintValidationCallback)
  }

  requestMoveItemToActiveSprint(status: Status) {
    this.pendingMoveToActiveSprintStatus = status
    this.$modal.show(this.moveToActiveSprintModalId)
  }

  async confirmMoveItemToActiveSprint() {
    const status = this.pendingMoveToActiveSprintStatus
    const itemId = this.item.id
    if (!status || !itemId) return

    const activeSprint = await this.getActiveSprintForMove()
    if (!activeSprint?.id) {
      this.resetMoveItemToActiveSprint()
      return
    }

    this.$modal.hide(this.moveToActiveSprintModalId)
    await this.onMoveToAndValid({
      from: this.item.getPriorityType,
      fromSprintId: this.item.getPriorityType === "SPRINT" ? this.item.sprint?.id : undefined,
      fromSprintInfo: this.item.getPriorityType === "SPRINT" ? this.item.sprint : undefined,
      to: "SPRINT",
      toSprintId: activeSprint.id,
      toSprintStatus: activeSprint.status,
      toSprintSequenceNumber: activeSprint.sequenceNumber,
      toSprintInfo: activeSprint,
      position: "BOTTOM",
      item: this.item
    })

    if (!this.canMoveToActiveSprint || this.sprintWipLimitExceeded) {
      return
    }

    await this.startMovedItemWork(status)
    this.pendingMoveToActiveSprintStatus = null
  }

  async getActiveSprintForMove() {
    const projectId = this.projectId
    if (
      (!this.teamHasActiveSprint?.sprintId || this.teamHasActiveSprint.status !== "STARTED") &&
      this.item.team?.id &&
      projectId
    ) {
      await this.hasActiveSprintCheck({
        teamId: this.item.team.id,
        projectId
      })
    }

    if (!this.teamHasActiveSprint?.sprintId || this.teamHasActiveSprint.status !== "STARTED") {
      return null
    }

    return await this.getSprint({
      sprintId: this.teamHasActiveSprint.sprintId,
      onlyGet: true
    })
  }

  async startMovedItemWork(status: Status) {
    const statusId = status.id === "RESUME" ? "IN_PROGRESS" : status.id
    if (statusId === "IN_PROGRESS" && Task.checkCompletionOfAllTasks(this.tasks)) {
      this.onShowModalCreateTask("IN_PROGRESS")
      return
    }
    await this.statusUpdate({
      status: statusId
    })
  }

  statusUpdate(_params: UpdateStatusParams) {
    //
  }

  resetMoveItemToActiveSprint() {
    this.$modal.hide(this.moveToActiveSprintModalId)
    this.pendingMoveToActiveSprintStatus = null
  }

  canAddItemIntoActiveSprint() {
    let valid = true
    const hasStoryPoints = this.item.storyPoints !== null
    const requiresStoryPoints = !this.item.isSupportCase && !this.item.sprintIsContinuous

    if (!this.estimatedTasks || (requiresStoryPoints && !hasStoryPoints)) {
      this.onShowModalEstimationRequired()
      valid = false
    }

    return valid
  }

  canMoveItemFromActiveSprint(payload: ItemMoveTo) {
    let valid = true
    if (
      payload.fromSprintInfo &&
      !BacklogSetup.canMoveItemFromSprint({
        sprintStatus: payload.fromSprintInfo.status,
        item: this.item
      })
    ) {
      if (!this.item.isCompleted) {
        this.setMoveItemFromActiveSprint(payload)
        this.$modal.show(this.removeItemFromSprintModalId)
      }
      valid = false
    }
    return valid
  }

  onShowModalEstimationRequired() {
    //
  }

  async sprintValidationByMethodAndAlert(payload: ItemMoveTo, validationMethod) {
    const { to, toSprintId, toSprintStatus, item, toSprintInfo } = payload

    if (!item.id) return

    this.canMoveToActiveSprint = true
    if (to === "SPRINT" && toSprintInfo && toSprintInfo.continuousSprint && !item.isSupportCase) {
      const checkWipLimit = await this.checkWipLimit({ itemId: item.id, sprintId: toSprintId })
      if (!checkWipLimit) {
        this.sprintWipLimitExceeded = toSprintInfo
        return
      }
    }
    if (to === "SPRINT" && toSprintStatus === "STARTED") {
      this.canMoveToActiveSprint = validationMethod()
    }

    if (!this.canMoveItemFromActiveSprint(payload)) {
      return
    }

    if (this.canMoveToActiveSprint) {
      let alertTo = ""

      if (to === "SPRINT" && toSprintInfo) {
        alertTo = this.$t("backlog.titles.backlogSprint") + `#${toSprintInfo.sequenceNumber}`
        this.updateOpenedItemValues({
          payload: [
            {
              id: "sprint",
              value: Sprint.create({
                id: toSprintId,
                sequenceNumber: toSprintInfo.sequenceNumber,
                status: toSprintStatus
              })
            }
          ] as unknown as UpdateObjectValue<Item>[]
        })
        this.updateOpenedItemValues({
          payload: [{ id: "sprintId", value: toSprintId }] as unknown as UpdateObjectValue<Item>[]
        })
      } else if (to === "BACKLOG") {
        alertTo = this.$t(`backlog.titlesByItemType['${item.itemType.toLowerCase()}']`)
        this.updateOpenedItemValues({
          payload: [
            { id: "backlogPriority", value: 0 },
            { id: "sprint", value: undefined }
          ] as unknown as UpdateObjectValue<Item>[]
        })
      } else if (to === "UNPRIORITIZED") {
        alertTo = this.$t("backlog.titles.backlogUnprioritized")
        this.updateOpenedItemValues({
          payload: [
            { id: "backlogPriority", value: null },
            { id: "sprint", value: undefined }
          ] as unknown as UpdateObjectValue<Item>[]
        })
      }

      this.showToast({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: [
          {
            text: "Item",
            type: "regular"
          },
          {
            text: ` #${item.sequenceNumber} `,
            type: "bold"
          },
          {
            text: "was moved to ",
            type: "regular"
          },
          {
            text: alertTo,
            type: "bold"
          }
        ]
      })
      await this.onMoveTo(payload)
    }
  }

  async onMoveTo(payload: ItemMoveTo) {
    if (!payload) {
      return
    }

    const {
      from,
      fromSprintId,
      to,
      toSprintId,
      position,
      item,
      nextItemId,
      previousItemId,
      newIndex,
      fromSprintInfo,
      message
    } = payload
    if (!item.id) {
      return
    }

    this.setCreatedBacklogItem(null)

    const getBacklogTypeByItem = (itemType: ItemType) => {
      const name = this.$t(`backlog.backlogQueryByItemType['${itemType.toLowerCase()}']`)
      const backlogName = this.$t(`backlog.main['${name}']`)
      return BacklogSetup[backlogName]
    }

    const moveTo = async () => {
      if (!item.id) return

      const payload: ItemQueryPriority = new ItemQueryPriority({
        itemId: item.id,
        priorityType: to,
        lowest: position === "BOTTOM",
        highest: position === "TOP",
        sprintId: toSprintId,
        fromSprintId: fromSprintId,
        nextItemId: nextItemId,
        previousItemId: previousItemId,
        message,
        projectId: item.projectId
      })
      let index: number | null = null
      if (newIndex === 0 || newIndex) {
        index = newIndex
      } else {
        index = position === "TOP" ? 0 : null
      }
      let { backlogPriority, sprintPriority } = item
      if (
        fromSprintInfo &&
        fromSprintInfo.status !== "COMPLETED" &&
        fromSprintInfo.status !== "HALTED"
      ) {
        if (BacklogSetup.BacklogSprint.removeAction) {
          const priority = await store.dispatch(
            Actions[BacklogSetup.BacklogSprint.removeAction],
            payload
          )
          if (toSprintId) {
            sprintPriority = priority.newPriority ?? null
          } else {
            backlogPriority = priority.newPriority ?? null
          }

          if (item.itemType === "SUPPORT_CASE" && from === "SPRINT" && to !== "SPRINT") {
            this.updateItemValue({
              id: "team",
              value: null
            } as any)
          }
        }
      } else if (to === "SPRINT") {
        if (BacklogSetup.BacklogSprint.addAction) {
          const priority = await store.dispatch(
            Actions[BacklogSetup.BacklogSprint.addAction],
            payload
          )
          sprintPriority = priority.newPriority ?? null
        }
      } else if (to === "BACKLOG") {
        if (from === "UNPRIORITIZED" && item.team?.id && item.projectId) {
          await this.hasActiveSprintCheck({ teamId: item.team.id, projectId: item.projectId })
          if (!this.teamHasActiveSprint?.sprintId) {
            this.updateItemValue({
              id: "team",
              value: null
            } as any)
          }
        }

        const priority = await store.dispatch(
          Actions[getBacklogTypeByItem(item.itemType).priorityAction],
          payload
        )
        sprintPriority = null
        backlogPriority = priority.newPriority ?? null
      } else if (to === "UNPRIORITIZED" && BacklogSetup.BacklogUnprioritized?.priorityAction) {
        await store.dispatch(Actions[BacklogSetup.BacklogUnprioritized.priorityAction], payload)
        sprintPriority = null
        backlogPriority = null
      }
      let backlogName = "backlogUnprioritized"

      switch (to) {
        case "SPRINT":
          backlogName = "backlogSprint"
          break
        case "BACKLOG":
          backlogName = getBacklogTypeByItem(item.itemTypeParsed.id).backlogType
          break
        case "UNPRIORITIZED":
          backlogName = "backlogUnprioritized"
          sprintPriority = null
          backlogPriority = null
          break
        default:
          backlogName = "backlogUnprioritized"
          sprintPriority = null
          backlogPriority = null
          break
      }

      this.itemMoveToParams = {
        index,
        item,
        backlogName,
        sprintId: toSprintId
      }
      item.sprintPriority = sprintPriority
      item.backlogPriority = backlogPriority
      this.canMoveToActiveSprint = true
    }

    if (from === to && fromSprintId === toSprintId) return

    if (!this.canMoveToActiveSprint) return
    await moveTo()
  }

  @Emit()
  getAllMetrics() {
    //
  }

  async onMoveItemToAnotherTeam(payload: ItemMoveTo) {
    const { item } = payload
    this.setItem(item)
    this.item.setInitialState()
    const updatedItem = await this.updateItem({ item: this.item, notSetItem: true })
    if (updatedItem) {
      this.updateItemValue({ id: "teamType", value: updatedItem.teamType } as any)
    }
    await this.onMoveToAndValid(payload)
  }

  insertItemInBacklog(payload: InsertItemInBacklog) {
    if (
      !this.backlogSetup ||
      (this.item.projectId &&
        this.sessionProjectId &&
        this.item.projectId !== this.sessionProjectId)
    )
      return

    const { item, backlogName, sprintId } = payload

    if (
      item instanceof Item &&
      backlogName === "backlogUnprioritized" &&
      this.unprioritizedFilter[0] !== item.itemType &&
      !(this.unprioritizedFilter[0] === "STORY" && item.itemType === "SPIKE")
    )
      return

    const itemStatus = (item.statusParsed?.id ?? item.status) as ItemStatus
    const backlogFilter = this.getTargetBacklogFilter(backlogName, sprintId)
    const isExcludedStatus = excludedStatusFromBacklog({
      backlogFilter,
      itemStatus
    })

    if (isExcludedStatus) {
      return
    }

    this.insertItem(payload)
  }

  goToSprintSettings(sequenceNumber) {
    const teamId = this.item.team?.urlName ?? this.$route.params.teamId
    this.$router.push({
      name: "edit-sprint",
      params: {
        sprintId: sequenceNumber,
        teamId
      }
    })
  }

  onHideModalWipLimitExceeded() {
    this.sprintWipLimitExceeded = null
  }

  async moveItemFromSprint(params: ItemMoveTo) {
    const { from, to, item, toSprintStatus, toSprintInfo } = params
    this.updateItemValue({
      id: "statusParsed",
      value: item.statusParsed
    } as any)
    this.updateItemValue({
      id: "statusChangeDescription",
      value: item.statusChangeDescription
    } as any)

    if (item.itemType === "SUPPORT_CASE" && from === "SPRINT" && to !== "SPRINT") {
      this.updateItemValue({
        id: "team",
        value: null
      } as any)
    }

    this.resetMoveItemFromSprint()

    const sprintValidationCallback = () => {
      if (toSprintStatus === "STARTED" && toSprintInfo) {
        this.setCreatedBacklogItem(Object.assign(item, { moveFrom: "moveTo" }))
        return this.canAddItemIntoActiveSprint()
      }
    }
    await this.sprintValidationByMethodAndAlert(params, sprintValidationCallback)
    if (!this.itemMoveToParams) return
    this.insertItem(this.itemMoveToParams)
    this.itemMoveToParams = null
  }

  resetMoveItemFromSprint() {
    this.$modal.hide(this.removeItemFromSprintModalId)
    this.setMoveItemFromActiveSprint(null)
  }
}
